import { useEffect, useMemo } from "react";
import { useBindActionCreators } from "@claroty/utilities/react/hooks";
import { useLocation, useRouteMatch } from "react-router-dom";
import { setBreadcrumbsParams } from "../../../actions/GeneralActions/generalActions";
import { URLS } from "../../../const/postAuthConsts";
import { BreadcrumbsOptionsTypes } from "../../app/BreadCrumbs/BreadcrumbsTypes";
import { PAGE_NAMES } from "../../app/pageUtils/pageConsts";
import { getPageNameParts, getPageTitleByUrl } from "../../app/pageUtils/pageUtils";

export type BreadcrumbsUpdaterProps = {
    showBreadcrumbs?: boolean;
    breadcrumbParams?: {
        lastPathName?: string;
    } & BreadcrumbsOptionsTypes;
    titleParam?: string;
    useLocationPath?: boolean;
};

function BreadcrumbsUpdater({
    showBreadcrumbs,
    breadcrumbParams,
    titleParam,
    useLocationPath,
}: BreadcrumbsUpdaterProps) {
    const actions = useBindActionCreators({ setBreadcrumbsParams });

    const match = useRouteMatch();
    const location = useLocation();

    const nameParts = useMemo(
        () => getPageNameParts(useLocationPath ? location.pathname : match.path),
        [match.path, location.pathname, location.pathname],
    );

    // breadcrumbs
    useEffect(() => {
        if (showBreadcrumbs) setBreadcrumbs(nameParts, actions, breadcrumbParams);
    }, [nameParts, breadcrumbParams, showBreadcrumbs]);

    // document.title
    useEffect(() => {
        document.title = getPageTitleByUrl(match.path, titleParam);
    }, [match.path, titleParam]);

    return null;
}

type PathPartsType = Array<{ text: string; link?: string }>;

/**
 * Build breadcrumbs from path location
 * @example /settings/admin/users -> Settings - Admin Panel - User Management
 */
function setBreadcrumbs(
    nameParts: PathPartsType,
    actions: {
        setBreadcrumbsParams: (pathParts: PathPartsType, options: any) => void;
    },
    {
        lastPathName = undefined,
        ...breadcrumbParams
    }: {
        lastPathName?: string;
        [breadcrumbParams: string]: any;
    } = {},
) {
    const breadcrumbs = [...nameParts];
    if (lastPathName) breadcrumbs.push({ text: lastPathName });
    breadcrumbs.unshift({ text: PAGE_NAMES[URLS.dashboard], link: URLS.dashboard });
    actions.setBreadcrumbsParams(breadcrumbs, breadcrumbParams);
}

export default BreadcrumbsUpdater;
